import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import { getTaskJobs, retryTaskJob, deleteTaskJob } from "api/apiService";
import { notifyCenter } from "utils/toast";

// Soft UI theme forces display:grid + placeItems:center on MuiInputBase-root,
// which shrinks the clickable area. Override so the whole Select opens the dropdown.
const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

const STATUS_META = {
  0: { label: "Venter", color: "warning", icon: HourglassTopIcon },
  1: { label: "Fullført", color: "success", icon: CheckCircleIcon },
  2: { label: "Feilet", color: "error", icon: ErrorIcon },
};

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function tryPretty(str) {
  if (!str) return "";
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return String(str);
  }
}

function StatusCard({ status, count }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <SoftBox display="flex" alignItems="center" justifyContent="space-between">
        <SoftBox>
          <SoftTypography variant="button" color="text" fontWeight="medium" textTransform="uppercase">
            {meta.label}
          </SoftTypography>
          <SoftTypography variant="h3" fontWeight="bold" color={meta.color}>
            {count}
          </SoftTypography>
        </SoftBox>
        <SoftBox
          sx={(t) => ({
            width: 56,
            height: 56,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: t.palette[meta.color]?.main || t.palette.grey[500],
            color: "white",
          })}
        >
          <Icon sx={{ fontSize: 28 }} />
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

StatusCard.propTypes = {
  status: PropTypes.oneOf([0, 1, 2]).isRequired,
  count: PropTypes.number.isRequired,
};

function TaskJobs() {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ 0: 0, 1: 0, 2: 0 });
  const [actions, setActions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const [detail, setDetail] = useState(null);
  const [retrying, setRetrying] = useState(null); // task-id being retried
  const [deleting, setDeleting] = useState(null); // task-id being deleted

  const offset = page * pageSize;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  async function handleRetry(task) {
    if (!task?.id) return;
    setRetrying(task.id);
    try {
      const res = await retryTaskJob(task.id);
      if (res?.ok) {
        notifyCenter("success", `Jobb #${task.id} satt tilbake til Venter`, { duration: 2500 });
        // Optimistic update: mark as pending locally
        setTasks((prev) =>
          prev.map((t) =>
            t.id === task.id
              ? { ...t, taskStatus: 0, taskStatusLabel: "pending", shortReport: null, statusReport: null }
              : t
          )
        );
        // Refresh counts in background
        fetchData();
      } else {
        notifyCenter("error", res?.message || res?.error || "Kunne ikke starte retry", { duration: 3000 });
      }
    } catch (e) {
      notifyCenter("error", e?.message || "Nettverksfeil", { duration: 3000 });
    } finally {
      setRetrying(null);
    }
  }

  async function handleDelete(task) {
    if (!task?.id) return;
    if (task.taskStatus !== 0) {
      notifyCenter("warning", "Kun ventende jobber kan slettes.", { duration: 2500 });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Slette ventende jobb #${task.id} (${task.taskAction})?`)) return;

    setDeleting(task.id);
    try {
      const res = await deleteTaskJob(task.id);
      if (res?.ok) {
        notifyCenter("success", `Jobb #${task.id} slettet`, { duration: 2500 });
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        fetchData();
      } else {
        notifyCenter("error", res?.message || res?.error || "Kunne ikke slette", { duration: 3000 });
      }
    } catch (e) {
      notifyCenter("error", e?.message || "Nettverksfeil", { duration: 3000 });
    } finally {
      setDeleting(null);
    }
  }

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const res = await getTaskJobs({
        status: statusFilter === "" ? undefined : Number(statusFilter),
        task_action: actionFilter || undefined,
        order_id: orderIdFilter || undefined,
        search: search || undefined,
        limit: pageSize,
        offset,
      });
      if (res?.ok) {
        setTasks(res.tasks || []);
        setTotal(res.total || 0);
        setStatusCounts(res.statusCounts || { 0: 0, 1: 0, 2: 0 });
        setActions(res.actions || []);
      } else {
        setError(res?.error || "Kunne ikke hente jobber");
      }
    } catch (e) {
      setError(e?.message || "Nettverksfeil");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, actionFilter]);

  function applyFilters() {
    setPage(0);
    fetchData();
  }

  function resetFilters() {
    setStatusFilter("");
    setActionFilter("");
    setOrderIdFilter("");
    setSearch("");
    setPage(0);
  }

  return (
    <DashboardLayout>
      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        {/* Status summary cards */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <StatusCard status={0} count={statusCounts[0] ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatusCard status={1} count={statusCounts[1] ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatusCard status={2} count={statusCounts[2] ?? 0} />
            </Grid>
          </Grid>
        </SoftBox>

        <Card>
          {/* Header */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                Jobber
              </SoftTypography>
              <SoftTypography variant="button" color="text">
                Bakgrunnsoppgaver (e-post, WC-synk, varsler)
              </SoftTypography>
            </SoftBox>
            <Tooltip title="Oppdater">
              <IconButton
                onClick={fetchData}
                disabled={loading}
                sx={(t) => ({
                  border: `1px solid ${t.palette.grey[300]}`,
                  borderRadius: "10px",
                })}
              >
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          {/* Filters */}
          <SoftBox p={3}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Status
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={statusFilter}
                    displayEmpty
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Alle statuser</MenuItem>
                    <MenuItem value={0}>Venter</MenuItem>
                    <MenuItem value={1}>Fullført</MenuItem>
                    <MenuItem value={2}>Feilet</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Handling
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={actionFilter}
                    displayEmpty
                    onChange={(e) => {
                      setActionFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Alle handlinger</MenuItem>
                    {actions.map((a) => (
                      <MenuItem key={a.action} value={a.action}>
                        {a.action} · {a.count}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Ordre-ID
                </SoftTypography>
                <SoftInput
                  placeholder="f.eks. 123"
                  value={orderIdFilter}
                  onChange={(e) => setOrderIdFilter(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Fritekstsøk
                </SoftTypography>
                <SoftInput
                  placeholder="Navn eller rapport"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </Grid>
            </Grid>
            <SoftBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <SoftButton variant="outlined" color="secondary" size="small" onClick={resetFilters}>
                Nullstill
              </SoftButton>
              <SoftButton variant="gradient" color="info" size="small" onClick={applyFilters}>
                Bruk filter
              </SoftButton>
            </SoftBox>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          {error && (
            <SoftBox px={3} py={2}>
              <SoftTypography variant="button" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          )}

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 1080,
                tableLayout: "fixed",
                // Override Soft UI theme: tableHead is forced to display:block,
                // which breaks column alignment. Restore standard table layout.
                "& thead": {
                  display: "table-header-group",
                  padding: 0,
                  borderRadius: 0,
                },
                "& tbody": { display: "table-row-group" },
                "& tr": { display: "table-row" },
                "& td, & th": {
                  display: "table-cell",
                  padding: "12px 14px",
                  borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
                  fontSize: 13,
                  verticalAlign: "middle",
                },
                "& thead th": {
                  backgroundColor: (t) => t.palette.grey[100],
                  color: (t) => t.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: 11,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                },
                "& tbody tr:hover": {
                  backgroundColor: (t) => t.palette.grey[50],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "6%" }}>ID</TableCell>
                  <TableCell sx={{ width: "18%" }}>Navn</TableCell>
                  <TableCell sx={{ width: "18%" }}>Handling</TableCell>
                  <TableCell sx={{ width: "11%" }}>Ordre</TableCell>
                  <TableCell sx={{ width: "9%" }}>Status</TableCell>
                  <TableCell sx={{ width: "auto" }}>Rapport</TableCell>
                  <TableCell sx={{ width: "13%" }}>Opprettet</TableCell>
                  <TableCell sx={{ width: "6%" }} align="center">Detaljer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <SoftTypography variant="body2" color="secondary">
                        Ingen jobber funnet
                      </SoftTypography>
                    </TableCell>
                  </TableRow>
                )}
                {tasks.map((t) => {
                  const meta = STATUS_META[t.taskStatus] || { label: String(t.taskStatus), color: "default" };
                  return (
                    <TableRow key={t.id}>
                      <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                        #{t.id}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: 13, textTransform: "none" }}>
                          {t.taskName}
                        </SoftTypography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="code"
                          sx={(theme) => ({
                            display: "inline-block",
                            fontSize: 11,
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: theme.palette.grey[100],
                            color: theme.palette.text.primary,
                            fontFamily: "monospace",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          })}
                        >
                          {t.taskAction}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {t.taskRecordId ? (
                          <SoftTypography variant="caption" color="text">
                            {t.taskTable} · {t.taskRecordId}
                          </SoftTypography>
                        ) : (
                          <SoftTypography variant="caption" color="secondary">
                            —
                          </SoftTypography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={meta.label}
                          color={meta.color}
                          size="small"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: 12,
                        }}
                      >
                        {t.shortReport || "—"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <SoftTypography variant="caption" color="text">
                            {formatDateTime(t.createdAt)}
                          </SoftTypography>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          {t.taskStatus === 2 && (
                            <Tooltip title="Kjør på nytt">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRetry(t)}
                                  disabled={retrying === t.id}
                                  sx={{ color: "warning.main" }}
                                >
                                  {retrying === t.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <ReplayIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {t.taskStatus === 0 && (
                            <Tooltip title="Slett ventende jobb">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(t)}
                                  disabled={deleting === t.id}
                                  sx={{ color: "error.main" }}
                                >
                                  {deleting === t.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Se detaljer">
                            <IconButton size="small" onClick={() => setDetail(t)}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

          {/* Footer pagination */}
          <Divider sx={{ my: 0 }} />
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftTypography variant="caption" color="secondary">
              Viser {tasks.length ? offset + 1 : 0}
              {"–"}
              {offset + tasks.length} av {total}
            </SoftTypography>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                Forrige
              </SoftButton>
              <SoftTypography variant="caption" color="text" px={1}>
                Side {page + 1} / {totalPages}
              </SoftTypography>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setPage((p) => p + 1)}
                disabled={offset + tasks.length >= total || loading}
              >
                Neste
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>

        {/* Detail dialog */}
        <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pr: 1,
            }}
          >
            <SoftBox>
              <SoftTypography variant="h6" fontWeight="bold">
                Jobb #{detail?.id}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                {detail?.taskName}
              </SoftTypography>
            </SoftBox>
            <IconButton onClick={() => setDetail(null)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {detail && (
              <SoftBox>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption" color="secondary" display="block">
                      Handling
                    </SoftTypography>
                    <SoftTypography variant="body2" fontFamily="monospace">
                      {detail.taskAction}
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption" color="secondary" display="block">
                      Status
                    </SoftTypography>
                    <Chip
                      label={STATUS_META[detail.taskStatus]?.label || detail.taskStatus}
                      color={STATUS_META[detail.taskStatus]?.color || "default"}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption" color="secondary" display="block">
                      Ordre
                    </SoftTypography>
                    <SoftTypography variant="body2">
                      {detail.taskTable} · {detail.taskRecordId || "—"}
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption" color="secondary" display="block">
                      Opprettet
                    </SoftTypography>
                    <SoftTypography variant="body2">
                      {formatDateTime(detail.createdAt)}
                    </SoftTypography>
                  </Grid>
                </Grid>

                <SoftTypography variant="caption" color="secondary" display="block" mb={0.5}>
                  Kort rapport
                </SoftTypography>
                <SoftBox
                  sx={(t) => ({
                    p: 2,
                    mb: 2,
                    borderRadius: 1,
                    backgroundColor: t.palette.grey[50],
                    border: `1px solid ${t.palette.grey[200]}`,
                    fontSize: 13,
                  })}
                >
                  {detail.shortReport || "—"}
                </SoftBox>

                <SoftTypography variant="caption" color="secondary" display="block" mb={0.5}>
                  Statusrapport
                </SoftTypography>
                <Box
                  component="pre"
                  sx={(t) => ({
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: t.palette.grey[900],
                    color: t.palette.common.white,
                    maxHeight: 400,
                    overflow: "auto",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                  })}
                >
                  {tryPretty(detail.statusReport) || "—"}
                </Box>

                {detail.taskStatus === 2 && (
                  <SoftBox display="flex" justifyContent="flex-end" mt={3}>
                    <SoftButton
                      variant="gradient"
                      color="warning"
                      size="small"
                      startIcon={
                        retrying === detail.id ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <ReplayIcon fontSize="small" />
                        )
                      }
                      disabled={retrying === detail.id}
                      onClick={() => {
                        handleRetry(detail);
                        setDetail(null);
                      }}
                    >
                      Kjør på nytt
                    </SoftButton>
                  </SoftBox>
                )}
                {detail.taskStatus === 0 && (
                  <SoftBox display="flex" justifyContent="flex-end" mt={3}>
                    <SoftButton
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={
                        deleting === detail.id ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )
                      }
                      disabled={deleting === detail.id}
                      onClick={() => {
                        handleDelete(detail);
                        setDetail(null);
                      }}
                    >
                      Slett jobb
                    </SoftButton>
                  </SoftBox>
                )}
              </SoftBox>
            )}
          </DialogContent>
        </Dialog>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TaskJobs;
